
import { Component, Prop, Provide, Vue } from "vue-property-decorator";
import {
  Control,
  OxygenControlsTreeContext,
  OxySelector
} from "@/oxygen/classes/lib/types";
import { updateSelectorProperties } from "@/oxygen/classes/lib/classes";
import OxygenControl from "@/oxygen/classes/OxygenControl.vue";
import OxygenControls from "@/oxygen/classes/OxygenControls.vue";
import { configStore, uiStore } from "@/store/store";
import { ClassProperties as ClassPropertiesT } from "@/oxygen/classes/lib/type-properties";
import { classControls } from "@/oxygen/classes/lib/controls";
import defensiveCopy from "@/util/defensive-copy";
import lodash from "lodash";
import NiceButton from "@/components/UI/NiceButton.vue";

@Component({
  components: {
    NiceButton,
    OxygenControls,
    OxygenControl
  }
})
export default class ClassProperties extends Vue {
  controls: Control[] = classControls;
  editAnyway = false;

  @Provide("oxyContext")
  readonly context: OxygenControlsTreeContext = this.reactiveContext();

  transactionKey: string | undefined | null = null;

  reactiveContext(): OxygenControlsTreeContext {
    return {
      getControlValue: controlPath => {
        const controlValue: Record<string, unknown> = {};

        for (const breakpoint of configStore.breakpoints) {
          const breakpointValue = lodash.get(
            this.properties,
            `${breakpoint.id}.${controlPath}`
          );

          if (breakpointValue !== undefined) {
            controlValue[breakpoint.id] = breakpointValue;
          }
        }

        return controlValue;
      },
      getControlValueAt: (controlPath, breakpoint) => {
        const fullPath = controlPath
          ? `${breakpoint}.${controlPath}`
          : breakpoint;

        return lodash.get(this.properties, fullPath);
      },
      getParentPath(controlPath) {
        return controlPath.split(".").slice(0, -1).join(".");
      },
      setControlValue: (controlPath, newValue, transactionKey) => {
        const newProperties = defensiveCopy(this.properties);
        const breakpoints = Object.keys(newValue);

        for (const breakpoint of breakpoints) {
          lodash.set(
            newProperties,
            `${breakpoint}.${controlPath}`,
            newValue[breakpoint]
          );
        }

        this.transactionKey = transactionKey;
        this.properties = newProperties;
      },
      setControlValueAt: (controlPath, breakpoint, value, transactionKey) => {
        const updatedProperties = defensiveCopy(this.properties);
        lodash.set(updatedProperties, `${breakpoint}.${controlPath}`, value);

        this.transactionKey = transactionKey;
        this.properties = updatedProperties;
      },
      getParentControlValueAt(controlPath, breakpoint) {
        const parentPath = this.getParentPath(controlPath);
        return this.getControlValueAt(parentPath, breakpoint);
      },
      getParentControlValue(controlPath) {
        const parentPath = this.getParentPath(controlPath);
        return this.getControlValue(parentPath);
      }
    };
  }

  @Prop()
  selector?: OxySelector;

  @Prop()
  nestedSelector?: OxySelector;

  get breakpoints() {
    return configStore.breakpoints;
  }

  get breakpointId() {
    return uiStore.currentBreakpoint.id;
  }

  get properties(): Record<string, unknown> {
    const source = this.nestedSelector || this.selector;

    if (!source || !source.properties) return {};
    return source.properties;
  }

  set properties(newProperties: Record<string, unknown>) {
    if (!this.selector) return;

    // Class properties can't be undefined for some reason,
    // so we need to set it to null and cast it to undefined.
    const transactionKey =
      this.transactionKey === null ? undefined : this.transactionKey;

    updateSelectorProperties({
      selectorId: this.selector.id,
      nestedSelectorId: this.nestedSelector?.id,
      properties: newProperties,
      transactionKey
    });
  }

  get propertiesAtCurrentBreakpoint() {
    return this.properties[this.breakpointId] as ClassPropertiesT;
  }

  set propertiesAtCurrentBreakpoint(newBreakpointProperties: ClassPropertiesT) {
    if (!this.selector) return;

    const currentProperties = defensiveCopy(this.properties);
    const isEmptyObj = Object.keys(newBreakpointProperties).length === 0;

    // Prevent saving empty object in the database as PHP will convert it to an array.
    currentProperties[this.breakpointId] = isEmptyObj
      ? null
      : newBreakpointProperties;

    this.properties = currentProperties;
  }
}
